// My Customers — full-screen managed table for the "My Customers" tile.
// Paginated (offset/limit) for large account books, with filter + sort + a
// per-row action dropdown. Reuses Person360Overlay.
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../../components/Layout";
import Person360Overlay from "../../components/Person360Overlay";
import {
  Users, Search, RefreshCw, Loader2, ChevronLeft, ChevronRight, ArrowLeft,
  ArrowUp, ArrowDown, ArrowUpDown, MoreHorizontal, FileText, ExternalLink,
  Package2, X,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const money = (n) =>
  n == null ? "—" : `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const relTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(+d)) return "—";
  const days = Math.round((Date.now() - +d) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "1d";
  if (days < 30) return `${days}d`;
  if (days < 365) return `${Math.round(days / 30)}mo`;
  return `${Math.round(days / 365)}y`;
};
const tierCls = (t) => {
  const k = String(t || "").toLowerCase();
  if (k.includes("black")) return "bg-slate-900 text-slate-100 border-slate-500/40";
  if (k.includes("plat")) return "bg-slate-200/10 text-slate-100 border-slate-300/40";
  if (k.includes("gold")) return "bg-amber-500/15 text-amber-300 border-amber-500/40";
  if (k.includes("silver")) return "bg-slate-400/15 text-slate-200 border-slate-400/40";
  if (k.includes("bronze")) return "bg-orange-800/20 text-orange-300 border-orange-700/40";
  return "border-white/10 text-slate-400";
};
const TIERS = ["Gold", "Silver", "Bronze", "Platinum", "Black"];

function SortTh({ label, k, sort, dir, onSort, align = "left" }) {
  const active = sort === k;
  return (
    <th className={`py-2 px-3 select-none ${align === "right" ? "text-right" : "text-left"}`}>
      <button
        type="button"
        onClick={() => onSort(k)}
        data-testid={`myc-th-${k}`}
        className={`inline-flex items-center gap-1 font-mono uppercase tracking-widest hover:text-white transition-colors ${active ? "text-teal-200" : "text-slate-400"} ${align === "right" ? "flex-row-reverse" : ""}`}
      >
        {label}
        {active ? (dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
      </button>
    </th>
  );
}

export default function MyCustomers() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [unassigned, setUnassigned] = useState(false);
  const [person360Id, setPerson360Id] = useState(null);
  const [menuId, setMenuId] = useState(null);

  // filters + sort + paging
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [tier, setTier] = useState("");
  const [needsMe, setNeedsMe] = useState(false);
  const [hasOrders, setHasOrders] = useState("");
  const [sort, setSort] = useState("last_active");
  const [dir, setDir] = useState("desc");
  const [pageSize, setPageSize] = useState(50);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // reset to page 0 when filters/sort change
  useEffect(() => { setOffset(0); }, [debounced, tier, needsMe, hasOrders, sort, dir, pageSize]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: pageSize, offset, sort, dir });
      if (debounced) p.set("q", debounced);
      if (tier) p.set("tier", tier);
      if (needsMe) p.set("needs_me", "true");
      if (hasOrders) p.set("has_orders", hasOrders);
      const r = await axios.get(`${API}/me/customers?${p.toString()}`, { withCredentials: true });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setUnassigned(!!r.data.unassigned);
    } catch {
      setItems([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [pageSize, offset, sort, dir, debounced, tier, needsMe, hasOrders]);
  useEffect(() => { load(); }, [load]);

  const onSort = (k) => {
    if (sort === k) { setDir((d) => (d === "asc" ? "desc" : "asc")); return; }
    setSort(k); setDir(k === "name" ? "asc" : "desc");
  };

  const page = Math.floor(offset / pageSize) + 1;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const activeFilters = (debounced ? 1 : 0) + (tier ? 1 : 0) + (needsMe ? 1 : 0) + (hasOrders ? 1 : 0);
  const clearAll = () => { setSearch(""); setTier(""); setNeedsMe(false); setHasOrders(""); };

  return (
    <Layout>
      <div className="space-y-5" data-testid="my-customers-page" onClick={() => setMenuId(null)}>
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <Link to="/my-dashboard" data-testid="myc-back" className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1 inline-flex items-center gap-1 hover:text-orange-300">
              <ArrowLeft className="w-3 h-3" /> My Dashboard
            </Link>
            <h1 className="text-3xl font-display font-bold tracking-tight flex items-center gap-3">
              <Users className="w-6 h-6 text-teal-300" /> My customers
              <span className="text-sm font-mono text-slate-500 tabular-nums" data-testid="myc-total">{total.toLocaleString()}</span>
            </h1>
            <p className="text-slate-400 text-sm mt-1">Everyone assigned to you. Sort, filter and act without leaving the page.</p>
          </div>
          <button type="button" onClick={load} disabled={loading} data-testid="myc-refresh"
            className="w-9 h-9 grid place-items-center rounded-lg border border-white/10 bg-white/[0.03] text-slate-300 hover:text-white hover:bg-white/[0.06] disabled:opacity-50" aria-label="Refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
        </div>

        {/* Filter bar */}
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-2.5" data-testid="myc-filters">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[240px] max-w-[420px]">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, company, email…"
                data-testid="myc-search"
                className="w-full pl-10 pr-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 focus:border-teal-400/60 outline-none text-sm text-white placeholder:text-slate-500" />
            </div>
            <div className="ml-auto flex items-center gap-2">
              <label className="text-[11px] font-mono uppercase tracking-widest text-slate-400">Show</label>
              <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} data-testid="myc-page-size"
                className="dark-select border border-white/10 rounded-md text-[12px] px-2 py-1 outline-none focus:border-teal-400/60">
                {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              {activeFilters > 0 && (
                <button type="button" onClick={clearAll} data-testid="myc-clear"
                  className="text-[11px] font-mono text-rose-300 hover:text-rose-200 inline-flex items-center gap-1">
                  <X className="w-3 h-3" /> Clear ({activeFilters})
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-[9.5px] font-mono uppercase tracking-widest text-slate-500 mr-1">Tier</span>
              {TIERS.map((t) => (
                <button key={t} type="button" onClick={() => setTier(tier === t ? "" : t)} data-testid={`myc-tier-${t}`}
                  className={`px-2 py-0.5 rounded-md text-[10.5px] font-mono uppercase tracking-widest border transition-colors ${tier === t ? "bg-teal-500/15 border-teal-500/50 text-teal-200" : "border-white/10 text-slate-400 hover:text-white hover:border-white/25"}`}>
                  {t}
                </button>
              ))}
            </div>
            <button type="button" onClick={() => setNeedsMe((v) => !v)} data-testid="myc-needsme"
              className={`px-2 py-0.5 rounded-md text-[10.5px] font-mono uppercase tracking-widest border transition-colors ${needsMe ? "bg-amber-500/15 border-amber-500/50 text-amber-200" : "border-white/10 text-slate-400 hover:text-white"}`}>
              Needs me
            </button>
            <div className="flex items-center gap-1">
              <span className="text-[9.5px] font-mono uppercase tracking-widest text-slate-500 mr-1">Orders</span>
              {[["true", "Has orders"], ["false", "No orders"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setHasOrders(hasOrders === v ? "" : v)} data-testid={`myc-orders-${v}`}
                  className={`px-2 py-0.5 rounded-md text-[10.5px] font-mono uppercase tracking-widest border transition-colors ${hasOrders === v ? "bg-sky-500/15 border-sky-500/50 text-sky-200" : "border-white/10 text-slate-400 hover:text-white"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
        </div>

        {unassigned && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.06] text-amber-200 text-sm px-3 py-2" data-testid="myc-unassigned">
            You don&rsquo;t have an account-manager identity set, so no customers can be matched to you yet.
          </div>
        )}

        {/* Table */}
        <div className="rounded-lg border border-white/10 overflow-hidden" data-testid="myc-table-wrap">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="myc-table">
              <thead className="bg-teal-500/10 border-b border-teal-500/20">
                <tr className="text-[10px] font-mono uppercase tracking-widest text-teal-200">
                  <SortTh label="Customer" k="name" sort={sort} dir={dir} onSort={onSort} />
                  <th className="py-2 px-3 text-left">Tier</th>
                  <SortTh label="LTV" k="ltv" sort={sort} dir={dir} onSort={onSort} align="right" />
                  <SortTh label="Orders" k="orders" sort={sort} dir={dir} onSort={onSort} align="right" />
                  <SortTh label="Last order" k="last_active" sort={sort} dir={dir} onSort={onSort} />
                  <th className="py-2 px-3 text-left">Flag</th>
                  <th className="py-2 px-3 text-right w-12">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="text-center py-16 text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-16 text-slate-500 text-sm" data-testid="myc-empty">
                    {activeFilters > 0 ? "No customers match these filters." : "No customers assigned to you yet."}
                  </td></tr>
                )}
                {!loading && items.map((c) => (
                  <tr key={c.id} data-testid={`myc-row-${c.id}`}
                    className="border-b border-white/[0.04] hover:bg-white/[0.03] cursor-pointer"
                    onClick={() => setPerson360Id(c.id)}>
                    <td className="py-2.5 px-3">
                      <div className="text-[13px] font-medium text-white truncate max-w-[260px]">{c.full_name || "—"}</div>
                      <div className="text-[11px] text-slate-500 truncate max-w-[260px]">
                        {c.company || c.email || "—"}
                      </div>
                    </td>
                    <td className="py-2.5 px-3">
                      {c.vip_tier
                        ? <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${tierCls(c.vip_tier)}`}>{c.vip_tier}</span>
                        : <span className="text-slate-600">—</span>}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums text-emerald-300">{money(c.ltv)}</td>
                    <td className="py-2.5 px-3 text-right tabular-nums text-slate-300">
                      <span className="inline-flex items-center gap-1 justify-end"><Package2 className="w-3 h-3 text-slate-500" />{c.orders_count ?? 0}</span>
                    </td>
                    <td className="py-2.5 px-3 text-slate-400 text-[12px] whitespace-nowrap">{relTime(c.last_order_date)}</td>
                    <td className="py-2.5 px-3">
                      {c.needs_me
                        ? <span className="text-[9px] font-mono uppercase tracking-widest text-amber-300">needs you</span>
                        : <span className="text-slate-600 text-xs">—</span>}
                    </td>
                    <td className="py-2.5 px-3 text-right relative" onClick={(e) => e.stopPropagation()}>
                      <button type="button" data-testid={`myc-actions-${c.id}`}
                        onClick={() => setMenuId(menuId === c.id ? null : c.id)}
                        className="w-7 h-7 grid place-items-center rounded text-slate-400 hover:text-white hover:bg-white/10">
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                      {menuId === c.id && (
                        <div className="absolute right-2 top-9 z-30 w-44 rounded-lg border border-white/15 bg-[#0B111C] shadow-2xl py-1" data-testid={`myc-menu-${c.id}`}>
                          <button type="button" onClick={() => { setMenuId(null); setPerson360Id(c.id); }}
                            data-testid={`myc-menu-360-${c.id}`}
                            className="w-full text-left px-3 py-1.5 text-[12px] text-slate-200 hover:bg-white/5 inline-flex items-center gap-2">
                            <ExternalLink className="w-3.5 h-3.5 text-teal-300" /> Open Customer 360
                          </button>
                          <button type="button" onClick={() => { setMenuId(null); navigate(`/quotes/new?customer=${c.id}`); }}
                            data-testid={`myc-menu-quote-${c.id}`}
                            className="w-full text-left px-3 py-1.5 text-[12px] text-slate-200 hover:bg-white/5 inline-flex items-center gap-2">
                            <FileText className="w-3.5 h-3.5 text-orange-300" /> New quote
                          </button>
                          <a href="/orders/dashboard"
                            data-testid={`myc-menu-orders-${c.id}`}
                            className="w-full text-left px-3 py-1.5 text-[12px] text-slate-200 hover:bg-white/5 inline-flex items-center gap-2">
                            <Package2 className="w-3.5 h-3.5 text-sky-300" /> View orders
                          </a>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between gap-3 px-3 py-2.5 border-t border-white/[0.06]" data-testid="myc-pagination">
            <span className="text-[11px] font-mono text-slate-500 tabular-nums">
              {total === 0 ? "0" : `${offset + 1}–${Math.min(offset + pageSize, total)}`} of {total.toLocaleString()}
            </span>
            <div className="flex items-center gap-1.5">
              <button type="button" data-testid="myc-prev" disabled={offset === 0 || loading}
                onClick={() => setOffset((o) => Math.max(0, o - pageSize))}
                className="w-8 h-8 grid place-items-center rounded-lg border border-white/10 text-slate-300 hover:text-white hover:bg-white/[0.06] disabled:opacity-30 disabled:cursor-default">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-[11px] font-mono text-slate-400 tabular-nums px-2" data-testid="myc-page">{page} / {pages}</span>
              <button type="button" data-testid="myc-next" disabled={offset + pageSize >= total || loading}
                onClick={() => setOffset((o) => o + pageSize)}
                className="w-8 h-8 grid place-items-center rounded-lg border border-white/10 text-slate-300 hover:text-white hover:bg-white/[0.06] disabled:opacity-30 disabled:cursor-default">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <Person360Overlay personId={person360Id} onClose={() => setPerson360Id(null)} />
    </Layout>
  );
}
